import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import type { components } from '@fluencelabs/vodopad-admin-api'
import { useTable } from '@refinedev/antd'
import type { HttpError } from '@refinedev/core'
import { Breadcrumb, Button, Space, Table } from 'antd'
import { useState } from 'react'

import client from '@/api/client'
import {
  BULK_TERMINATE_CONFIRM_TEXT,
  BULK_TERMINATE_DESCRIPTION,
  TerminateConfirmModal,
} from '@/components/common/TerminateConfirmModal'
import { PUBLIC_IP_TERMINAL_STATUSES } from '@/constants/statuses'
import {
  labelsForIds,
  useResourceTermination,
} from '@/hooks/useResourceTermination'
import { useTableRowNavigation } from '@/hooks/useTableRowNavigation'
import { UsersCacheProvider } from '@/hooks/useUsersByIds'
import { getPersistedPageSize, tablePaginationConfig } from '@/types/api'
import { searchByNameOrId } from '@/utils/searchFilters'

import { publicIpColumns } from './columns'
import { CreatePublicIpModal } from './components/CreatePublicIpModal'
import { NetworkFilterForm } from './components/NetworkFilterForm'

type UserPublicIpDto = components['schemas']['UserPublicIpDto']

export default function NetworkListPage() {
  const {
    selectedRowKeys,
    setSelectedRowKeys,
    terminateTarget,
    setTerminateTarget,
    doTerminate,
    bulkTerminateOpen,
    openBulkTerminate,
    closeBulkTerminate,
  } = useResourceTermination<UserPublicIpDto>({
    resourceLabel: 'IP',
    terminateFn: (ids) =>
      client.POST('/v1/admin/public_ips/terminate', {
        body: { publicIpIds: ids },
      }),
  })
  const onRow = useTableRowNavigation('public-ips')
  const [createOpen, setCreateOpen] = useState(false)

  const { tableProps, searchFormProps, filters } = useTable<
    UserPublicIpDto,
    HttpError,
    {
      search?: string
      address?: string
      status?: string
      userId?: string
      clusterId?: string
    }
  >({
    resource: 'public-ips',
    pagination: { pageSize: getPersistedPageSize() },
    syncWithLocation: true,
    onSearch: (values) => [
      ...searchByNameOrId(values.search, 'ids', true),
      {
        field: 'address',
        operator: 'eq',
        value: values.address?.trim() || undefined,
      },
      { field: 'status', operator: 'eq', value: values.status || undefined },
      { field: 'userId', operator: 'eq', value: values.userId || undefined },
      {
        field: 'clusterId',
        operator: 'eq',
        value: values.clusterId || undefined,
      },
    ],
  })

  return (
    <div>
      <Breadcrumb style={{ marginBottom: 16 }} items={[{ title: 'Network' }]} />
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: 16,
        }}
      >
        <Space>
          {selectedRowKeys.length > 0 && (
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={openBulkTerminate}
            >
              Terminate ({selectedRowKeys.length})
            </Button>
          )}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateOpen(true)}
          >
            Create Public IP
          </Button>
        </Space>
      </div>

      <NetworkFilterForm searchFormProps={searchFormProps} filters={filters} />

      <UsersCacheProvider
        ids={(tableProps.dataSource ?? []).map((row) => row.userId)}
      >
        <Table
          {...tableProps}
          pagination={tablePaginationConfig(tableProps)}
          rowKey="id"
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys as string[]),
            getCheckboxProps: (record: UserPublicIpDto) => ({
              disabled: PUBLIC_IP_TERMINAL_STATUSES.has(record.status),
            }),
          }}
          onRow={onRow}
          columns={publicIpColumns(setTerminateTarget)}
          scroll={{ x: 700 }}
        />
      </UsersCacheProvider>

      <CreatePublicIpModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />

      <TerminateConfirmModal
        open={terminateTarget !== null}
        title="Terminate Public IP"
        resourceName={terminateTarget?.name ?? ''}
        onConfirm={() => doTerminate([terminateTarget?.id ?? ''])}
        onCancel={() => setTerminateTarget(null)}
      />

      <TerminateConfirmModal
        open={bulkTerminateOpen}
        title={`Terminate ${selectedRowKeys.length} public IP(s)`}
        targets={labelsForIds(selectedRowKeys, tableProps.dataSource)}
        confirmText={BULK_TERMINATE_CONFIRM_TEXT}
        description={BULK_TERMINATE_DESCRIPTION}
        onConfirm={() => doTerminate(selectedRowKeys)}
        onCancel={closeBulkTerminate}
      />
    </div>
  )
}
